<template>
    <div class="form-container">
        <questionnaires
            :multiple="false"
            :searchable="false"
            :defaultSelecteds="false"
            :hideSelectedOptions="false"
            :state="questionnaireState"
            queryScopes="notSelfOnly,hasEvaluateds"
            :model.sync="$v.form.questionnaire.$model"
        />
        <network-types-checkboxes
            v-if="hasNetworkTypesOptions"
            class="mt-4"
            :state="networkTypesState"
            :isLoading="loadingQuestionnaireInfo"
            :options.sync="networkTypesOptions"
            :selected.sync="$v.form.networkTypes.$model"
        />
        <b-form-group v-if="hasUsersOptions" class="mt-4">
            <h5 class="mb-2">
                {{ $t('talk.modal.receiver') }}
            </h5>
            <multiselect-color
                :id="`users-multiselect`"
                :options.sync="usersOptions"
                :placeholder="$t('talk.modal.collaborator')"
                :loading="loadingQuestionnaireInfo"
                :clearOnSelect="false"
                :closeOnSelect="false"
                :searchable="false"
                :multiple="true"
                :hideSelected="true"
                :state="usersState"
                :selected.sync="$v.form.users.$model"
                :numberOfSelectedOptionsToDisplay="5"
                color="#022B4F"
            />
        </b-form-group>
        <templates-select
            class="mt-4"
            @template-selected="onChangedTemplateHandler"
        />
        <div v-if="form.asks">
            <ask
                v-for="(ask, index) in form.asks"
                :key="`ask-${index}`"
                :ask="ask"
                :createNewIfEdit.sync="ask.create"
                @ask-created="onAskCreatedHandler"
                @delete-ask="onAskDeletedHandler"
            />
        </div>
        <new-ask :templateId="null" @created="onAskCreatedHandler" />
        <b-form-invalid-feedback :state="asksState">
            {{ $t('talk.modal.invalidAsks') }}
        </b-form-invalid-feedback>
        <div class="footer">
            <b-form-invalid-feedback :state="formState">
                {{ $t('talk.modal.invalid') }}
            </b-form-invalid-feedback>
            <b-button
                size="sm"
                variant="primary"
                :disabled="loading"
                @click="onClickHandler"
            >
                <span v-if="!loading">
                    {{ $t('form.button.send') }}
                </span>
                <i class="fa fa-spinner fa-spin px-2" v-if="loading"></i>
            </b-button>

            <span v-if="loading" class="text-muted small">
                {{ $t('talk.modal.loading') }}
            </span>
        </div>
    </div>
</template>

<script>
import { Questionnaires } from '@/views/values/components'
import { NetworkTypesCheckboxes, TemplatesSelect } from './components'
import { Ask, NewAsk } from '@/views/talk/templates/components'
import { UsersSelect } from '@/views/talk/feedbacks/children/components/filters/components'
import { validationMixin } from 'vuelidate'
import { required } from 'vuelidate/lib/validators'
import { getQuestionnaireInfo, requestFeedbacksWithQuestionnaire } from '../api'
import notification, { success, danger } from '@/lib/notification'
import { mapperToOptions } from '@/views/components/selects/select-with-avatar'
import { MultiselectColor } from '@/views/components/selects'

notification.init()

export default {
    name: 'OthersForm',
    components: {
        Questionnaires,
        NetworkTypesCheckboxes,
        TemplatesSelect,
        UsersSelect,
        Ask,
        NewAsk,
        MultiselectColor,
    },
    mixins: [validationMixin],
    data() {
        return {
            form: {
                questionnaire: null,
                users: [],
                networkTypes: [],
                asks: [],
            },
            template: null,
            loading: false,
            loadingQuestionnaireInfo: false,
            questionnaire: {
                evaluateds: [],
                networkTypes: [],
            },
        }
    },
    validations: {
        form: {
            questionnaire: {
                required,
            },
            users: {
                required,
            },
            networkTypes: {
                required,
            },
            asks: {
                required,
            },
        },
    },
    methods: {
        onChangedTemplateHandler(template) {
            this.template = template
            if (!template) {
                this.$v.form.asks.$model = null
                return
            }
            this.$v.form.asks.$model = template.asks.map((e) => {
                return {
                    ...e,
                    create: true,
                }
            })
        },
        onAskCreatedHandler(ask) {
            if (!ask.previousId) {
                return this.addAskToArray(ask)
            }
            return this.changeAskInArray(ask)
        },
        addAskToArray(ask) {
            this.$v.form.asks.$model.push(ask)
        },
        changeAskInArray(ask) {
            const index = this.getIndexFromId(ask.previousId)
            this.$v.form.asks.$model[index] = ask

            const newAsks = [...this.form.asks]
            this.$v.form.asks.$model = newAsks
        },
        onAskDeletedHandler(askId) {
            const index = this.getIndexFromId(askId)
            this.$v.form.asks.$model.splice(index, 1)
        },
        getIndexFromId(id) {
            return this.form.asks.map((e) => e.id).indexOf(id)
        },
        checkState(property) {
            const field = this.$v.form[property]
            return field.$dirty ? !field.$invalid : null
        },
        checkFormState() {
            return this.$v.form.$dirty ? !this.$v.form.$invalid : null
        },
        onClickHandler() {
            this.$v.$touch()
            if (this.$v.form.$invalid) {
                return
            }

            const form = this.getFormObject()

            this.loading = true
            requestFeedbacksWithQuestionnaire(form)
                .then(() => {
                    success(this.$t('talk.modal.success'))
                })
                .catch((e) => {
                    console.log(e)
                    danger()
                })
                .finally(() => {
                    this.loading = false
                    this.$emit('close-modal')
                })
        },
        getFormObject() {
            return {
                asks: this.form.asks.map((e) => e.id),
                users: this.form.users.map((e) => e.value),
                networkTypes: this.form.networkTypes,
                questionnaire: this.form.questionnaire.value,
            }
        },
        getUsersOptionsMapped() {
            const options = mapperToOptions(this.questionnaire.evaluateds)
            return options.map((u) => ({
                ...u,
                color: {
                    hex: '#022B4F',
                },
            }))
        },
        getNetworkTypesOptions() {
            const networkTypesOptions = this.questionnaire.networkTypes
            return networkTypesOptions.filter((n) => n.type !== 'self')
        },
        async setQuestionnaireInfo() {
            this.loadingQuestionnaireInfo = true

            const { evaluateds, network_types } = await getQuestionnaireInfo(
                this.form.questionnaire.value
            )

            this.questionnaire = {
                evaluateds,
                networkTypes: network_types,
            }

            this.loadingQuestionnaireInfo = false
        },
        resetForm() {
            const fields = ['users', 'networkTypes']
            fields.forEach((field) => {
                if (this.$v.form[field].$model.length) {
                    this.$v.form[field].$model = []
                }
            })
        },
    },
    watch: {
        'form.questionnaire': function () {
            this.setQuestionnaireInfo()
            this.resetForm()
        },
    },
    computed: {
        usersOptions() {
            return this.getUsersOptionsMapped()
        },
        networkTypesOptions() {
            return this.getNetworkTypesOptions()
        },
        hasNetworkTypesOptions() {
            return this.networkTypesOptions.length > 0
        },
        hasUsersOptions() {
            return this.usersOptions.length > 0
        },
        questionnaireState() {
            return this.checkState('questionnaire')
        },
        networkTypesState() {
            return this.checkState('networkTypes')
        },
        usersState() {
            return this.checkState('users')
        },
        asksState() {
            return this.checkState('asks')
        },
        formState() {
            return this.checkFormState()
        },
    },
}
</script>

<style lang="sass" scoped>
$darkBlue: #022B4F
::v-deep
    h5,
    .font-weight-bold
        font-size: 16px
        font-weight: 400 !important
        color: #23282c
    #custom-multiselect .is-valid
        border-color: $darkBlue !important

.form-container
    padding: 15px 0
    font-size: 15px
    color: black
    font-weight: 400
    .footer
        width: 100%
        display: flex
        justify-content: center
        align-items: flex-end
        flex-direction: column
        margin-top: 20px
        button
            width: 78px
    ::v-deep
        color: #23282c
        font-size: 15px !important
</style>
